import { createFileRoute } from '@tanstack/react-router'
import { useMemo, useState } from 'react'
import { Stack } from '@astryxdesign/core/Stack'
import { Card } from '@astryxdesign/core/Card'
import { Text } from '@astryxdesign/core/Text'
import { Grid } from '@astryxdesign/core/Grid'
import { Button } from '@astryxdesign/core/Button'
import { Badge } from '@astryxdesign/core/Badge'
import { Banner } from '@astryxdesign/core/Banner'
import { TextInput } from '@astryxdesign/core/TextInput'
import { ToggleButton } from '@astryxdesign/core/ToggleButton'
import { useCategories, useExpenses, useMe, useTrip } from '#/lib/hooks'
import { enqueue } from '#/lib/sync'
import { BUILTIN_CATEGORIES, CATEGORY_ICONS } from '#/lib/types'

export const Route = createFileRoute('/manage/categories')({ component: Categories })

/**
 * The six built-ins are constants, not rows: they cannot be removed, so the
 * ledger always has somewhere to put a meal even on a phone that has never
 * synced. Everything below that is the party's own, shared with everyone.
 */
function Categories() {
  const trip = useTrip()
  const me = useMe()
  const all = useCategories(trip?.id)
  const expenses = useExpenses(trip?.id)
  const [label, setLabel] = useState('')
  const [icon, setIcon] = useState(CATEGORY_ICONS[0])
  const [error, setError] = useState<string | null>(null)

  const custom = all.filter((c) => !BUILTIN_CATEGORIES.some((b) => b.id === c.id))

  // How much is already filed under each, so removing one is a decision made
  // with the number in front of you rather than a surprise afterwards.
  const counts = useMemo(() => {
    const m = new Map<string, number>()
    for (const e of expenses) m.set(e.category, (m.get(e.category) ?? 0) + 1)
    return m
  }, [expenses])

  const taken = all.some((c) => c.label.toLowerCase() === label.trim().toLowerCase())
  const canAdd = label.trim().length > 0 && !taken

  async function add() {
    if (!canAdd || !trip || !me) return
    setError(null)
    await enqueue({
      table: 'expense_categories',
      op: 'upsert',
      row: {
        id: `c_${crypto.randomUUID()}`,
        trip_id: trip.id,
        label: label.trim(),
        icon,
        sort_order: custom.length,
        created_by: me.id,
      },
    })
    setLabel('')
  }

  async function remove(id: string) {
    await enqueue({ table: 'expense_categories', op: 'delete', row: { id } })
  }

  return (
    <Stack direction="vertical" gap={4}>
      <Stack direction="vertical" gap={0}>
        <Text type="display-3" display="block">Categories</Text>
        <Text type="supporting">
          Added here, these show up in the spend sheet on everyone's phone.
        </Text>
      </Stack>

      {error && <Banner status="error" title={error} />}

      <Card>
        <Stack direction="vertical" gap={3}>
          <Text type="label" color="secondary">Add one</Text>
          <TextInput
            label="Name"
            value={label}
            onChange={setLabel}
            placeholder="Camel ride"
            status={taken ? { type: 'error', message: 'There is already one with that name.' } : undefined}
          />
          <Stack direction="vertical" gap={1}>
            <Text type="supporting">Icon</Text>
            <Grid columns={5} gap={2}>
              {CATEGORY_ICONS.map((e) => (
                <ToggleButton
                  key={e}
                  size="sm"
                  label={`Icon ${e}`}
                  isIconOnly
                  icon={<Text type="inherit">{e}</Text>}
                  isPressed={icon === e}
                  onPressedChange={() => setIcon(e)}
                />
              ))}
            </Grid>
          </Stack>
          <Button
            label="Add category"
            variant="primary"
            width="100%"
            isDisabled={!canAdd}
            clickAction={add}
          />
        </Stack>
      </Card>

      <Stack direction="vertical" gap={2}>
        <Text type="label" color="secondary">Yours</Text>
        {custom.length === 0 && (
          <Text type="supporting">
            None yet — the six built-in ones cover most of a trip.
          </Text>
        )}
        {custom.map((c) => {
          const used = counts.get(c.id) ?? 0
          return (
            <Card key={c.id} padding={3}>
              <Stack direction="horizontal" align="center" justify="between" gap={3}>
                <Stack direction="horizontal" align="center" gap={3}>
                  <Text type="large">{c.icon}</Text>
                  <Stack direction="vertical" gap={0}>
                    <Text weight="medium">{c.label}</Text>
                    <Text type="supporting">
                      {used === 0 ? 'Not used yet' : `${used} ${used === 1 ? 'expense' : 'expenses'}`}
                    </Text>
                  </Stack>
                </Stack>
                <Button label="Remove" variant="ghost" onClick={() => remove(c.id)} />
              </Stack>
            </Card>
          )
        })}
        {custom.some((c) => (counts.get(c.id) ?? 0) > 0) && (
          <Text type="supporting">
            Removing one leaves its expenses alone — they keep the amount and
            the note, and stop being grouped under it.
          </Text>
        )}
      </Stack>

      <Stack direction="vertical" gap={2}>
        <Text type="label" color="secondary">Built in</Text>
        {/* A row of six badges ran off the side of a phone and truncated every
            one of them to three letters. */}
        <Grid columns={3} gap={2}>
          {BUILTIN_CATEGORIES.map((c) => (
            <Badge key={c.id} label={`${c.icon} ${c.label}`} variant="neutral" />
          ))}
        </Grid>
        <Text type="supporting">These are always here and cannot be removed.</Text>
      </Stack>
    </Stack>
  )
}
